import {
  getFirestore,
  collection,
  getDocs,
  onSnapshot,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  addDoc,
  query,
  orderBy,
  arrayUnion,
  Timestamp,
  Firestore,
} from 'firebase/firestore';
import type {
  IUserDataService,
  UserDocSnapshot,
  Unsubscribe,
} from '@breath-lab/services';
import type {
  UserAchievement,
  UserJourney,
  BreathworkWorkout,
} from '@breath-lab/types';
import { firebaseApp } from '../firebase/firebaseConfig';
import {
  FIRESTORE_COLLECTIONS,
  userAchievementsPath,
  userJourneysPath,
  userWorkoutsPath,
} from '../firebase/firestoreCollections';
import {
  userAchievementFromFirestore,
  userJourneyFromFirestore,
  userJourneyToFirestore,
  workoutFromFirestore,
  workoutToFirestore,
} from '../firebase/firestoreParsers';

export class WebUserDataService implements IUserDataService {
  private db: Firestore;

  constructor() {
    this.db = getFirestore(firebaseApp);
  }

  // -------------------------------------------------------------------------
  // User doc
  // -------------------------------------------------------------------------

  async fetchUserDoc(uid: string): Promise<UserDocSnapshot | null> {
    const ref = doc(this.db, FIRESTORE_COLLECTIONS.usersPrivateData, uid);
    const snap = await getDoc(ref);
    return snap.exists() ? (snap.data() as UserDocSnapshot) : null;
  }

  async writeUserDoc(uid: string, data: UserDocSnapshot): Promise<void> {
    const ref = doc(this.db, FIRESTORE_COLLECTIONS.usersPrivateData, uid);
    await setDoc(ref, data, { merge: true });
  }

  async mergeUserDoc(uid: string, partial: UserDocSnapshot): Promise<void> {
    const ref = doc(this.db, FIRESTORE_COLLECTIONS.usersPrivateData, uid);
    await updateDoc(ref, partial);
  }

  listenToUserDoc(uid: string, onUpdate: (data: UserDocSnapshot) => void): Unsubscribe {
    const ref = doc(this.db, FIRESTORE_COLLECTIONS.usersPrivateData, uid);
    return onSnapshot(ref, (snap) => {
      if (snap.exists()) onUpdate(snap.data() as UserDocSnapshot);
    });
  }

  // -------------------------------------------------------------------------
  // Achievements
  // -------------------------------------------------------------------------

  async fetchUserAchievements(uid: string): Promise<UserAchievement[]> {
    const snap = await getDocs(collection(this.db, userAchievementsPath(uid)));
    return snap.docs.map((d) => userAchievementFromFirestore({ id: d.id, ...d.data() }));
  }

  listenUserAchievements(
    uid: string,
    onChange: (achievements: UserAchievement[]) => void,
  ): Unsubscribe {
    return onSnapshot(collection(this.db, userAchievementsPath(uid)), (snap) => {
      const achievements = snap.docs.map((d) =>
        userAchievementFromFirestore({ id: d.id, ...d.data() }),
      );
      onChange(achievements);
    });
  }

  // -------------------------------------------------------------------------
  // Journeys
  // -------------------------------------------------------------------------

  async fetchUserJourneys(uid: string): Promise<UserJourney[]> {
    const snap = await getDocs(
      query(collection(this.db, userJourneysPath(uid)), orderBy('startedAt', 'desc')),
    );
    return snap.docs.map((d) => userJourneyFromFirestore({ id: d.id, ...d.data() }));
  }

  async saveUserJourney(uid: string, uj: UserJourney): Promise<string> {
    const ref = await addDoc(
      collection(this.db, userJourneysPath(uid)),
      userJourneyToFirestore(uj),
    );
    return ref.id;
  }

  async updateUserJourney(
    uid: string,
    journeyId: string,
    data: Partial<UserJourney>,
  ): Promise<void> {
    await updateDoc(
      doc(this.db, userJourneysPath(uid), journeyId),
      data as Record<string, unknown>,
    );
  }

  async deleteUserJourney(uid: string, journeyId: string): Promise<void> {
    await deleteDoc(doc(this.db, userJourneysPath(uid), journeyId));
  }

  async recordStepCompleted(
    uid: string,
    userJourneyDocId: string,
    stepId: string,
    nextStepIndex: number,
    isJourneyComplete: boolean,
  ): Promise<void> {
    const now = Timestamp.now();
    await updateDoc(doc(this.db, userJourneysPath(uid), userJourneyDocId), {
      completedSteps: arrayUnion({ stepId, completedAt: now }),
      currentStepIndex: nextStepIndex,
      lastPracticedAt: now,
      ...(isJourneyComplete && { status: 'completed', completedAt: now }),
    });
  }

  async abandonUserJourney(uid: string, docId: string): Promise<void> {
    await updateDoc(doc(this.db, userJourneysPath(uid), docId), { status: 'abandoned' });
  }

  async restartUserJourney(uid: string, docId: string): Promise<void> {
    const now = Timestamp.now();
    await updateDoc(doc(this.db, userJourneysPath(uid), docId), {
      status: 'active',
      completedSteps: [],
      currentStepIndex: 0,
      startedAt: now,
      lastPracticedAt: null,
      completedAt: null,
    });
  }

  // -------------------------------------------------------------------------
  // Workouts
  // -------------------------------------------------------------------------

  async saveWorkout(uid: string, workout: BreathworkWorkout): Promise<string> {
    const ref = await addDoc(
      collection(this.db, userWorkoutsPath(uid)),
      workoutToFirestore(workout),
    );
    return ref.id;
  }

  async updateWorkoutFeelings(
    uid: string,
    workoutId: string,
    postFeelingsIds: string[],
  ): Promise<void> {
    await updateDoc(doc(this.db, userWorkoutsPath(uid), workoutId), { postFeelingsIds });
  }

  async fetchWorkouts(uid: string): Promise<BreathworkWorkout[]> {
    const snap = await getDocs(
      query(collection(this.db, userWorkoutsPath(uid)), orderBy('startedAt', 'desc')),
    );
    return snap.docs.map((d) => workoutFromFirestore({ id: d.id, ...d.data() }));
  }
}
